import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { dashboardService } from '../services/dashboardService';
import { projectService } from '../services/projectService';
import {
  CCard, CCardBody, CCardHeader, CCardTitle,
  CRow, CCol,
  CTable, CTableHead, CTableBody, CTableRow, CTableHeaderCell, CTableDataCell,
  CBadge, CButton, CFormSelect, CAlert, CSpinner,
} from '@coreui/react';
import AgingBadge from '../components/AgingBadge';
import DrillDownDialog from '../components/DrillDownDialog';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { Users, AlertTriangle, Clock, TrendingUp, Download, UserSearch, CheckCircle, RefreshCw, DatabaseZap } from 'lucide-react';

const STATUS_LABELS = {
  pending_approval: 'Pending Approval', approved_not_started: 'Approved / Not Started',
  job_posting: 'Job Posting', interview_scheduled: 'Interview Scheduled',
  waiting_interview_result: 'Waiting for Result', offer_sent: 'Offer Sent',
  accepted_waiting_start: 'Accepted / Waiting', started: 'Started', cancelled_on_hold: 'Cancelled',
};
const AGING_COLORS = { '0-7': '#16a34a', '8-14': '#ca8a04', '15-30': '#ea580c', '>30': '#dc2626' };
const STATUS_CHART_COLORS = ['#321fdb', '#3399ff', '#f9b115', '#e55353', '#2eb85c', '#9da5b1', '#e44dd2'];

export default function DashboardPage() {
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [byStatus, setByStatus] = useState([]);
  const [byAging, setByAging] = useState([]);
  const [byProject, setByProject] = useState([]);
  const [byPosition, setByPosition] = useState([]);
  const [candidatePool, setCandidatePool] = useState(null);
  const [projects, setProjects] = useState([]);
  const [areas, setAreas] = useState([]);
  const [positions, setPositions] = useState([]);
  const [syncInfo, setSyncInfo] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  const [drillDown, setDrillDown] = useState({ open: false, title: '', data: [], loading: false });

  const api = authAxios();
  const showPool = ['hr', 'rm', 'director'].includes(user?.role);
  const canSync = false;

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterProject) params.project_id = filterProject;
      if (filterArea) params.area = filterArea;
      if (filterPosition) params.position_id = filterPosition;

      const reqs = [
        dashboardService.getSummary(api, params),
        dashboardService.getByStatus(api, params),
        dashboardService.getByAging(api, params),
        dashboardService.getByProject(api, params),
        dashboardService.getByPosition(api, params),
        dashboardService.getSyncStatus(api),
        projectService.getProjects(api),
        projectService.getAreas(api),
        projectService.getPositions(api),
      ];
      if (showPool) reqs.push(dashboardService.getCandidatePool(api, { position_id: params.position_id }));

      const res = await Promise.all(reqs);
      setSummary(res[0].data);
      setByStatus(res[1].data);
      setByAging(res[2].data);
      setByProject(res[3].data);
      setByPosition(res[4].data);
      setSyncInfo(res[5].data);
      setProjects(res[6].data);
      setAreas(res[7].data);
      setPositions(res[8].data);
      if (showPool && res[9]) setCandidatePool(res[9].data);
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  };

  const handleSync = async () => {
    try {
      setSyncLoading(true);
      const res = await dashboardService.syncGoogleSheet(api, {});
      toast.success(`Synced ${res.data.recruitment_cases || 0} recruitment cases from Google Sheet`);
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to sync Google Sheet');
    } finally {
      setSyncLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [filterProject, filterArea, filterPosition]);

  const openDrillDown = async (title, params) => {
    setDrillDown({ open: true, title, data: [], loading: true });
    try {
      const res = await dashboardService.getDrillDown(api, params);
      setDrillDown({ open: true, title, data: res.data, loading: false });
    } catch { setDrillDown({ open: true, title, data: [], loading: false }); }
  };

  const clearFilters = () => { setFilterProject(''); setFilterArea(''); setFilterPosition(''); };
  const hasFilter = filterProject || filterArea || filterPosition;

  if (loading && !summary) {
    return <div className="text-center py-5" data-testid="dashboard-loading"><CSpinner color="primary" /></div>;
  }

  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white shadow-sm border rounded p-2 small">
        <div className="fw-medium">{label}</div>
        {payload.map((p, i) => <div key={i} className="text-body-secondary">{p.name}: <strong>{p.value}</strong></div>)}
      </div>
    );
  };

  return (
    <div data-testid="dashboard-page">
      <CCard className="mb-4 border-0 shadow-sm overflow-hidden" data-testid="dashboard-hero" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 55%, #38bdf8 100%)' }}>
        <CCardBody className="p-4 text-white">
          <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 align-items-lg-center">
            <div>
              <div className="text-uppercase fw-semibold mb-2" style={{ letterSpacing: '0.18em', fontSize: '11px', opacity: 0.75 }}>Smart Recruit Command Center</div>
              <h3 className="fw-bold mb-2">Live hiring control room for `Tracking new 2026` and `Application 2026`</h3>
              <div className="small" style={{ opacity: 0.88, maxWidth: '760px' }}>
                ดูภาพรวมตำแหน่งที่ยังขาด คนที่ยังหาอยู่ อายุเคสสรรหา และ candidate pool พร้อม sync ข้อมูลล่าสุดจาก Google Sheet ได้ตรงจากหน้า dashboard
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <CBadge color="light" textColor="dark" shape="rounded-pill" className="px-3 py-2">
                Open cases {summary?.open_cases || 0}
              </CBadge>
              <CBadge color="warning" textColor="dark" shape="rounded-pill" className="px-3 py-2">
                Over 30 days {summary?.cases_over_30_days || 0}
              </CBadge>
              <CButton color="light" size="sm" onClick={() => dashboardService.exportCSV(api)} data-testid="export-dashboard-btn">
                <Download size={14} className="me-1" /> Export CSV
              </CButton>
              {canSync && (
                <CButton color="info" size="sm" className="text-white" onClick={handleSync} disabled={syncLoading} data-testid="sync-google-sheet-btn">
                  {syncLoading ? <RefreshCw size={14} className="me-1 spin" /> : <DatabaseZap size={14} className="me-1" />}
                  {syncLoading ? 'Syncing...' : 'Sync Google Sheet'}
                </CButton>
              )}
            </div>
          </div>
          <div className="d-flex flex-wrap gap-3 mt-3 small" style={{ opacity: 0.9 }}>
            <span>Last import: {syncInfo?.synced_at ? new Date(syncInfo.synced_at).toLocaleString() : 'manual app mode'}</span>
            <span>Tracking: {syncInfo?.tracking_sheet || '-'}</span>
            <span>Application: {syncInfo?.application_sheet || '-'}</span>
          </div>
        </CCardBody>
      </CCard>

      {/* Filters */}
      <CCard className="mb-4 border-0 shadow-sm" data-testid="dashboard-filters">
        <CCardBody className="py-2">
          <CRow className="g-2 align-items-center">
            <CCol xs="auto"><small className="text-body-secondary">Filters:</small></CCol>
            <CCol sm={3} md={2}>
              <CFormSelect size="sm" value={filterProject} onChange={e => setFilterProject(e.target.value)} data-testid="filter-project">
                <option value="">All Projects</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </CFormSelect>
            </CCol>
            <CCol sm={2} md={2}>
              <CFormSelect size="sm" value={filterArea} onChange={e => setFilterArea(e.target.value)} data-testid="filter-area">
                <option value="">All Areas</option>
                {areas.map(a => <option key={a} value={a}>{a}</option>)}
              </CFormSelect>
            </CCol>
            <CCol sm={3} md={2}>
              <CFormSelect size="sm" value={filterPosition} onChange={e => setFilterPosition(e.target.value)} data-testid="filter-position">
                <option value="">All Positions</option>
                {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </CFormSelect>
            </CCol>
            {hasFilter && (
              <CCol xs="auto">
                <CButton color="ghost-secondary" size="sm" onClick={clearFilters} data-testid="clear-filters">Clear</CButton>
              </CCol>
            )}
          </CRow>
        </CCardBody>
      </CCard>

      {/* Warning */}
      {summary?.has_gap_without_cases && (
        <CAlert color="warning" className="d-flex align-items-start gap-2 mb-4" data-testid="gap-warning">
          <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
          <div>
            <strong>Missing Manpower Without Recruitment Cases</strong>
            <div className="small">{summary.gap_without_cases} position(s) missing but not yet in the recruitment pipeline.</div>
          </div>
        </CAlert>
      )}

      {/* KPI Cards */}
      <CRow className="g-3 mb-4" data-testid="kpi-cards">
        <CCol sm={6} xl={3}>
          <CCard className="border-0 shadow-sm border-top border-top-3 border-danger cursor-pointer" role="button"
            onClick={() => openDrillDown('All Missing People', {})} data-testid="kpi-total-missing">
            <CCardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-body-secondary text-uppercase fw-semibold" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Total Missing</small>
                <Users size={16} className="text-danger" />
              </div>
              <div className="fs-1 fw-bold">{summary?.total_missing || 0}</div>
              <small className="text-body-tertiary">people needed right now</small>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} xl={3}>
          <CCard className="border-0 shadow-sm border-top border-top-3 border-primary cursor-pointer" role="button"
            onClick={() => openDrillDown('All Open Cases', {})} data-testid="kpi-open-cases">
            <CCardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-body-secondary text-uppercase fw-semibold" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Open Cases</small>
                <TrendingUp size={16} className="text-primary" />
              </div>
              <div className="fs-1 fw-bold">{summary?.open_cases || 0}</div>
              <small className="text-body-tertiary">active recruitment cases</small>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} xl={3}>
          <CCard className="border-0 shadow-sm border-top border-top-3 border-warning cursor-pointer" role="button"
            onClick={() => openDrillDown('Cases Over 30 Days', { aging_group: '>30' })} data-testid="kpi-over-30">
            <CCardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-body-secondary text-uppercase fw-semibold" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Over 30 Days</small>
                <Clock size={16} className="text-warning" />
              </div>
              <div className="fs-1 fw-bold text-danger">{summary?.cases_over_30_days || 0}</div>
              <small className="text-body-tertiary">cases need attention</small>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} xl={3}>
          <CCard className="border-0 shadow-sm border-top border-top-3 border-success" data-testid="kpi-headcount">
            <CCardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-body-secondary text-uppercase fw-semibold" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Headcount</small>
                <Users size={16} className="text-success" />
              </div>
              <div className="fs-1 fw-bold">{summary?.total_actual || 0}<span className="fs-5 text-body-tertiary fw-normal">/{summary?.total_required || 0}</span></div>
              <small className="text-body-tertiary">actual vs required</small>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Pool KPI */}
      {showPool && candidatePool && (
        <CRow className="g-3 mb-4" data-testid="pool-kpi-cards">
          <CCol sm={6}>
            <CCard className="border-0 shadow-sm border-start border-start-4 border-info cursor-pointer" role="button"
              onClick={() => navigate('/candidate-pool')} data-testid="kpi-pool-total">
              <CCardBody>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <small className="text-body-secondary text-uppercase fw-semibold" style={{ fontSize: '11px' }}>Pool Candidates</small>
                  <UserSearch size={16} className="text-info" />
                </div>
                <div className="fs-1 fw-bold">{candidatePool.total || 0}</div>
                <small className="text-body-tertiary">candidates in the pool</small>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol sm={6}>
            <CCard className="border-0 shadow-sm border-start border-start-4 border-success cursor-pointer" role="button"
              onClick={() => navigate('/candidate-pool')} data-testid="kpi-pool-passed">
              <CCardBody>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <small className="text-body-secondary text-uppercase fw-semibold" style={{ fontSize: '11px' }}>Ready to Deploy</small>
                  <CheckCircle size={16} className="text-success" />
                </div>
                <div className="fs-1 fw-bold text-success">{candidatePool.passed || 0}</div>
                <small className="text-body-tertiary">passed candidates ready</small>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      )}

      {/* Charts */}
      <CRow className="g-3 mb-4">
        <CCol lg={8}>
          <CCard className="border-0 shadow-sm" data-testid="chart-by-status">
            <CCardBody>
              <CCardTitle className="small fw-semibold mb-3">Missing People by Status</CCardTitle>
              {byStatus.length === 0 ? <p className="text-body-secondary text-center py-4">No open cases</p> : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={byStatus} layout="vertical" margin={{ left: 110, right: 20, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#9da5b1' }} />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 11, fill: '#636f83' }} width={110} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="qty" name="People" radius={[0, 4, 4, 0]} maxBarSize={24}>
                      {byStatus.map((e, i) => (
                        <Cell key={e.status} fill={STATUS_CHART_COLORS[i % STATUS_CHART_COLORS.length]}
                          className="cursor-pointer" onClick={() => openDrillDown(`Status: ${e.label}`, { status: e.status })} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol lg={4}>
          <CCard className="border-0 shadow-sm" data-testid="chart-by-aging">
            <CCardBody>
              <CCardTitle className="small fw-semibold mb-3">Missing People by Aging</CCardTitle>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={byAging} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                  <XAxis dataKey="group" tick={{ fontSize: 11, fill: '#636f83' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#9da5b1' }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="qty" name="People" radius={[4, 4, 0, 0]} maxBarSize={40}>
                    {byAging.map(e => (
                      <Cell key={e.group} fill={AGING_COLORS[e.group] || '#9da5b1'}
                        className="cursor-pointer" onClick={() => openDrillDown(`Aging: ${e.group} days`, { aging_group: e.group })} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Tables */}
      <CRow className="g-3 mb-4">
        <CCol lg={6}>
          <CCard className="border-0 shadow-sm" data-testid="table-by-project">
            <CCardBody>
              <CCardTitle className="small fw-semibold mb-3">Missing by Project</CCardTitle>
              <CTable hover small>
                <CTableHead color="light">
                  <CTableRow>
                    <CTableHeaderCell className="small text-body-secondary">Project</CTableHeaderCell>
                    <CTableHeaderCell className="small text-body-secondary text-center">Req</CTableHeaderCell>
                    <CTableHeaderCell className="small text-body-secondary text-center">Act</CTableHeaderCell>
                    <CTableHeaderCell className="small text-body-secondary text-center">Gap</CTableHeaderCell>
                    <CTableHeaderCell className="small text-body-secondary text-center">Cases</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {byProject.map(p => (
                    <CTableRow key={p.project_id} className="cursor-pointer"
                      onClick={() => openDrillDown(`Project: ${p.project_name}`, { project_id: p.project_id })}>
                      <CTableDataCell className="small fw-medium">{p.project_name}</CTableDataCell>
                      <CTableDataCell className="small text-center font-monospace">{p.required}</CTableDataCell>
                      <CTableDataCell className="small text-center font-monospace">{p.actual}</CTableDataCell>
                      <CTableDataCell className="text-center">
                        <CBadge color={p.gap > 0 ? 'danger' : 'success'} shape="rounded-pill">{p.gap}</CBadge>
                      </CTableDataCell>
                      <CTableDataCell className="small text-center font-monospace">{p.open_cases}</CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol lg={6}>
          <CCard className="border-0 shadow-sm" data-testid="table-by-position">
            <CCardBody>
              <CCardTitle className="small fw-semibold mb-3">Missing by Position</CCardTitle>
              <CTable hover small>
                <CTableHead color="light">
                  <CTableRow>
                    <CTableHeaderCell className="small text-body-secondary">Position</CTableHeaderCell>
                    <CTableHeaderCell className="small text-body-secondary text-center">Req</CTableHeaderCell>
                    <CTableHeaderCell className="small text-body-secondary text-center">Act</CTableHeaderCell>
                    <CTableHeaderCell className="small text-body-secondary text-center">Gap</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {byPosition.map(p => (
                    <CTableRow key={p.position_id} className="cursor-pointer"
                      onClick={() => openDrillDown(`Position: ${p.position_name}`, { position_id: p.position_id })}>
                      <CTableDataCell className="small fw-medium">{p.position_name}</CTableDataCell>
                      <CTableDataCell className="small text-center font-monospace">{p.required}</CTableDataCell>
                      <CTableDataCell className="small text-center font-monospace">{p.actual}</CTableDataCell>
                      <CTableDataCell className="text-center">
                        <CBadge color={p.gap > 0 ? 'danger' : 'success'} shape="rounded-pill">{p.gap}</CBadge>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Pool Tables */}
      {showPool && candidatePool && (
        <CRow className="g-3 mb-4">
          <CCol lg={6}>
            <CCard className="border-0 shadow-sm border-start border-start-4 border-info" data-testid="pool-by-position">
              <CCardBody>
                <CCardTitle className="small fw-semibold mb-3 d-flex align-items-center gap-2">
                  <UserSearch size={14} className="text-info" /> Pool by Position
                </CCardTitle>
                <CTable hover small>
                  <CTableHead color="light">
                    <CTableRow>
                      <CTableHeaderCell className="small text-body-secondary">Position</CTableHeaderCell>
                      <CTableHeaderCell className="small text-body-secondary text-center">Total</CTableHeaderCell>
                      <CTableHeaderCell className="small text-body-secondary text-center">Passed</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {candidatePool.by_position?.map(p => (
                      <CTableRow key={p.position_id} className="cursor-pointer" onClick={() => navigate('/candidate-pool')}>
                        <CTableDataCell className="small fw-medium">{p.position_name}</CTableDataCell>
                        <CTableDataCell className="small text-center font-monospace">{p.total}</CTableDataCell>
                        <CTableDataCell className="text-center">
                          <CBadge color="success" shape="rounded-pill">{p.passed}</CBadge>
                        </CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol lg={6}>
            <CCard className="border-0 shadow-sm border-start border-start-4 border-info" data-testid="pool-by-zone">
              <CCardBody>
                <CCardTitle className="small fw-semibold mb-3 d-flex align-items-center gap-2">
                  <UserSearch size={14} className="text-info" /> Pool by Work Zone
                </CCardTitle>
                <CTable hover small>
                  <CTableHead color="light">
                    <CTableRow>
                      <CTableHeaderCell className="small text-body-secondary">Zone</CTableHeaderCell>
                      <CTableHeaderCell className="small text-body-secondary text-center">Total</CTableHeaderCell>
                      <CTableHeaderCell className="small text-body-secondary text-center">Passed</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {candidatePool.by_zone?.map(z => (
                      <CTableRow key={z.zone} className="cursor-pointer" onClick={() => navigate('/candidate-pool')}>
                        <CTableDataCell className="small fw-medium">{z.zone}</CTableDataCell>
                        <CTableDataCell className="small text-center font-monospace">{z.total}</CTableDataCell>
                        <CTableDataCell className="text-center">
                          <CBadge color="success" shape="rounded-pill">{z.passed}</CBadge>
                        </CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      )}

      {/* Status Breakdown */}
      <CCard className="border-0 shadow-sm mb-4" data-testid="table-status-breakdown">
        <CCardBody>
          <CCardTitle className="small fw-semibold mb-3">Open Cases by Status</CCardTitle>
          <CTable hover small>
            <CTableHead color="light">
              <CTableRow>
                <CTableHeaderCell className="small text-body-secondary">Status</CTableHeaderCell>
                <CTableHeaderCell className="small text-body-secondary text-center">Cases</CTableHeaderCell>
                <CTableHeaderCell className="small text-body-secondary text-center">People</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {byStatus.map(s => (
                <CTableRow key={s.status} className="cursor-pointer"
                  onClick={() => openDrillDown(`Status: ${s.label}`, { status: s.status })}>
                  <CTableDataCell className="small fw-medium">{s.label}</CTableDataCell>
                  <CTableDataCell className="small text-center font-monospace">{s.count}</CTableDataCell>
                  <CTableDataCell className="text-center">
                    <span className="fw-bold text-primary cursor-pointer">{s.qty}</span>
                  </CTableDataCell>
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        </CCardBody>
      </CCard>

      <DrillDownDialog open={drillDown.open} onClose={() => setDrillDown({ ...drillDown, open: false })}
        title={drillDown.title} data={drillDown.data} loading={drillDown.loading} />
    </div>
  );
}
